import Marked from "marked-react";
import {useEffect, useRef, useState} from "react";
import logo from "../../assets/images/logo-planejadora-icon.png";
import logoWhite from "../../assets/images/logo-planejadora-icon-white.png";
import logoSendMessage from "../../assets/images/icons/send-message.svg";
import {eventTag, getCurrentCNPJ} from "../../services/utils";
import "./index.scss";
import ApiGeneral from "../../services/general";
import {useApp} from "../../hooks/use-app";

const event = {
  name: "click-lis",
  category: "LIS",
  action: "click",
};

export default function ChatLIS() {
  const {companies} = useApp()

  const [dialogId, setDialogId] = useState(() => {
    return localStorage.getItem("dialogId") || null;
  });

  const inputRef = useRef(null);
  const messageListRef = useRef(null);
  const parsedLis = JSON.parse(process.env.REACT_APP_LIS);
  const urlLis = parsedLis.url;
  const token = parsedLis.token;
  const agente = "80e58f2b-6e44-4ac0-9ad3-93d39e7b1040";

  // As vezes a LIS me retorna um "Thought", que seria desnecessário adicionar na conversa
  // Essa função filtra e exibe somente a resposta final, o que considero que seja o que deve ser exibido
  function cleanThoughts(text) {
    const finalAnswer = text.match(/Final Answer:(.*)/s);
    if (finalAnswer) {
      return finalAnswer[1];
    }
    return text;
  }

  const formatMarkdown = (text) => {
    return text
      .replace(/---/g, "\n\n---\n\n")
      .replace(/### /g, "\n### ")
      .replace(/- /g, "\n- ");
  };

  const [inputValue, setInputValue] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const historical = localStorage.getItem("historicalLIS");
    return historical
      ? JSON.parse(historical)
      : [{ from: "lis", text: "Oi! Como eu posso te ajudar?" }];
  });
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);

  const toggleChat = () => {
    setIsChatOpen((prev) => !prev);
    eventTag(event?.name, event?.category, event?.action, "Chat");
    ApiGeneral().sendDigitalMoments('access_lis', {
      cnpj: getCurrentCNPJ(companies) || null
    })
  };
  const closeChat = () => {
    setIsChatOpen(false);
    eventTag(event?.name, event?.category, event?.action, "Fechar chat");
  };

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isWaitingResponse) return;

    const newMessages = [...messages, { from: "user", text: trimmed }];
    setMessages(newMessages);
    setInputValue("");
    setIsWaitingResponse(true);
    setMessages((prev) => [
      ...prev,
      { from: "lis", text: "Digitando...", typing: true },
    ]);

    inputRef.current?.focus();

    try {
      const dialogParam = dialogId ? `&dialog_id=${dialogId}` : "";
      const response = await fetch(
        `${urlLis}/smartchatbot/dialog/?assistant_profile_id=${agente}${dialogParam}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: trimmed,
          }),
        }
      );

      const data = await response.json();

      if (!dialogId && data?.id) {
        setDialogId(data.id);
        localStorage.setItem("dialogId", data.id);
      }

      const lastInteraction =
        data.dialog_interaction?.[data.dialog_interaction.length - 1];
      const botReply =
        lastInteraction?.ai_response ||
        "Desculpe, não estou podendo lhe ajudar com isso no momento.";

      const answerWithoutThoughts = cleanThoughts(botReply);

      setMessages((prev) => [
        ...prev.filter((msg) => !msg.typing),
        { from: "lis", text: answerWithoutThoughts },
      ]);
      eventTag(event?.name, event?.category, "submit", "Envio de mensagem");
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      setMessages((prev) => [
        ...prev.filter((msg) => !msg.typing),
        {
          from: "lis",
          text: "Não entendo sua pergunta, ou você poderia repetir?",
        },
      ]);
    } finally {
      setIsWaitingResponse(false);
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    const container = messageListRef.current;
    if (container && isChatOpen) {
      container.scrollTop = container.scrollHeight;
    }
    localStorage.setItem("historicalLIS", JSON.stringify(messages));
  }, [messages, isChatOpen]);

  return (
    <div className="chat-lis-wrapper" style={{ zIndex: "6" }}>
      {!isChatOpen && (
        <button
          id="open-modal-lis-sebrae"
          className="btn btn-blue open-modal-lis-sebrae"
          onClick={toggleChat}
        >
          <img src={logoWhite} alt="avatar da LIS" />
          {/* <span>Olá! Sou a LIS e estarei aqui para te ajudar.</span> */}
        </button>
      )}

      {isChatOpen && (
        <div className="modal-lis-sebrae">
          <div className="chat-header">
            <img src={logoWhite} alt="avatar" />
            <span>Olá! Sou a Assistente de IA e estarei aqui para te ajudar.</span>
            <button className="close-chat" onClick={closeChat}>
              ×
            </button>
          </div>
          <div className="chat-box">
            <div className="message-list" ref={messageListRef}>
              <div className="message-container">
                {messages.map((msg, index) => {
                  const isShortBotMessage =
                    msg.from === "lis" &&
                    msg.text.replace(/\s+/g, "").length < 33;

                  return (
                    <div
                      key={index}
                      className={`message ${
                        msg.from === "lis" ? "from-lis" : "from-user"
                      }`}
                    >
                      {msg.from === "lis" && <img src={logo} alt="avatar" />}
                      <div
                        className={`message-bubble ${
                          isShortBotMessage ? "bubble-margin-top" : ""
                        }`}
                      >
                        {msg.typing ? (
                          <div className="typing-indicator">
                            <svg
                              style={{
                                display: "block",
                                shapeRendering: "auto",
                              }}
                              width="50px"
                              height="15px"
                              viewBox="0 0 100 12"
                            >
                              <circle cx="35" cy="10" r="5" fill="#fff">
                                <animate
                                  attributeName="cy"
                                  calcMode="spline"
                                  keySplines="0 0.5 0.5 1;0.5 0 1 0.5;0.5 0.5 0.5 0.5"
                                  repeatCount="indefinite"
                                  values="11.5;8.5;11.5;11.5"
                                  keyTimes="0;0.3;0.6;1"
                                  dur="0.7s"
                                  begin="-0.42s"
                                />
                              </circle>
                              <circle cx="50" cy="10" r="5" fill="#fff">
                                <animate
                                  attributeName="cy"
                                  calcMode="spline"
                                  keySplines="0 0.5 0.5 1;0.5 0 1 0.5;0.5 0.5 0.5 0.5"
                                  repeatCount="indefinite"
                                  values="11.5;8.5;11.5;11.5"
                                  keyTimes="0;0.3;0.6;1"
                                  dur="0.7s"
                                  begin="-0.28s"
                                />
                              </circle>
                              <circle cx="65" cy="10" r="5" fill="#fff">
                                <animate
                                  attributeName="cy"
                                  calcMode="spline"
                                  keySplines="0 0.5 0.5 1;0.5 0 1 0.5;0.5 0.5 0.5 0.5"
                                  repeatCount="indefinite"
                                  values="11.5;8.5;11.5;11.5"
                                  keyTimes="0;0.3;0.6;1"
                                  dur="0.7s"
                                  begin="-0.14s"
                                />
                              </circle>
                            </svg>
                          </div>
                        ) : msg.from === "lis" ? (
                          <Marked>{formatMarkdown(msg.text)}</Marked>
                        ) : (
                          <p>{msg.text}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="chat-input-area">
              <input
                ref={inputRef}
                type="text"
                placeholder="Faça uma pergunta"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !isWaitingResponse && handleSend()
                }
              />

              <button onClick={handleSend} disabled={isWaitingResponse}>
                <img src={logoSendMessage} alt="enviar" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
